import { Navigate, redirect, useRouter } from '@tanstack/react-router';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import ActivityLogsPage from './index.page';
import type { ActivityLogTab } from '@/features/activity-feed';

/** The two Activity Logs URL segments. `activity_logs` is the legacy spelling. */
export type ActivityLogsSegment = 'activity_logs' | 'profile_activity';

const TAB_ROUTES = {
  activity_logs: '/workspaces/$workspaceId/journeys/activity_logs/$tab',
  profile_activity: '/workspaces/$workspaceId/journeys/profile_activity/$tab',
} as const;

/**
 * Index `beforeLoad`: land on this segment's own default (`identified`) tab. Deliberately
 * static — no feature-flag read — so it can't race the account load. Segment
 * canonicalization (activity_logs ↔ profile_activity) happens in the tab view below, after
 * the rename stage is known.
 */
export function redirectToSegmentDefaultTab(
  segment: ActivityLogsSegment,
  params: { workspaceId?: WorkspaceIdRouteParamInput },
): void {
  throw redirect({
    to: TAB_ROUTES[segment],
    // The workspace route's params.stringify normalizes the id when the location is built.
    params: { workspaceId: params.workspaceId ?? 'last', tab: 'identified' },
    search: true,
  });
}

/**
 * Renders the Activity Logs page at the canonical `profile_activity` segment, and forwards the
 * legacy `activity_logs` segment to it.
 */
type ActivityLogsRouteViewProps = {
  segment: ActivityLogsSegment;
  workspaceId: WorkspaceIdRouteParamInput;
  tab: string;
};

export function ActivityLogsRouteView({ segment, workspaceId: workspaceIdParam, tab }: ActivityLogsRouteViewProps) {
  const router = useRouter();
  const workspaceId = normalizeWorkspaceIdRouteParam(workspaceIdParam);
  const canonicalSegment: ActivityLogsSegment = 'profile_activity';
  const currentTab: ActivityLogTab = tab === 'anonymous' ? 'anonymous' : 'identified';

  if (segment !== canonicalSegment) {
    return <Navigate to={TAB_ROUTES[canonicalSegment]} params={{ workspaceId, tab: currentTab }} search replace />;
  }

  // Build each tab's href on the canonical segment, preserving the current filters (search params).
  // `TabNav` renders these as real links and routes them through the app router (client-side nav).
  const tabHref = (next: ActivityLogTab) =>
    router.buildLocation({
      to: TAB_ROUTES[canonicalSegment],
      params: { workspaceId, tab: next },
      search: (prev) => prev,
    }).href;

  return (
    <RumPageRendered>
      <ActivityLogsPage workspaceId={workspaceId} tab={currentTab} tabHref={tabHref} />
    </RumPageRendered>
  );
}
